import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import { Brief, BriefRecipientContact } from '@disco/types/types/brief'
import {
  Box,
  DataTable,
  Dialog,
  Text,
  Flex,
  Button,
  SplitButton,
} from 'components/ds'
import { BriefCard } from './brief-card'
import createColumns from './tableConfig'
import { RowSelectionState } from '@disco/types/types/data-table'
import { toggleRecipientBriefLink } from '@disco/state-management/reducers/entities/briefs'
import mixpanel from '@disco/api/mixpanel'

type ManageAccessModalProps = {
  onClose: () => void
  brief: Brief
  briefRecipients: BriefRecipientContact[]
  loading: boolean
  onShareWithRecipients?: (emails: string[]) => void
}

export const ManageAccessModal: React.FC<ManageAccessModalProps> = ({
  onClose,
  brief,
  briefRecipients,
  loading,
  onShareWithRecipients,
}) => {
  const dispatch = useDispatch()

  // use to prevent showing loading state caused by enable / disable links after initial load
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(
    !!briefRecipients.length,
  )
  const hasStartedLoading = useRef(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const hasSelectedRecipients = Object.keys(rowSelection).length > 0

  useEffect(() => {
    if (loading && !hasStartedLoading.current) {
      hasStartedLoading.current = true
    } else if (!loading && hasStartedLoading.current && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [loading, hasInitiallyLoaded])

  if (briefRecipients.length === 0 && !loading) {
    return 'No recipients have access to this brief.'
  }

  const briefRecipientsTableData = briefRecipients.map(recipient => ({
    ...recipient,
    id: recipient.contact_id,
    briefId: brief.id,
  }))

  const columns = createColumns(
    dispatch,
    onShareWithRecipients,
    rowSelection,
    setRowSelection,
  )

  const recipientsTable = (
    <DataTable
      columns={columns}
      data={briefRecipientsTableData}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      rowSelectionCondition={row =>
        !!row.original.link_enabled && !row.original.isUpdating
      }
    />
  )

  const buttonDropdownItems = [
    {
      label: 'Disable link',
      onSelect: () => {
        const recipientsToDisable = briefRecipientsTableData
          .filter(row => rowSelection[row.id])
          .map(r => ({
            contact_id: r.contact_id,
            saved_recipients_id: r.saved_recipients_id,
            enabled: false,
          }))

        dispatch(
          toggleRecipientBriefLink({
            briefId: brief.id,
            recipients: recipientsToDisable,
          }),
        )
        mixpanel.sendPureEvent('Brief recipient link toggled', {
          Context: 'Briefs',
          brief_id: brief.id,
          bulk: true,
          enabled: false,
        })

        setRowSelection({})
      },
    },
  ]

  const handleShare = () => {
    const shareEmails = briefRecipientsTableData
      .filter(row => rowSelection[row.id])
      .map(r => r.email)

    if (onShareWithRecipients) {
      onShareWithRecipients(shareEmails)
    }
  }

  return (
    <Dialog.Root defaultOpen onOpenChange={onClose}>
      <Dialog.Content maxWidth="900px" aria-describedby={undefined}>
        <Dialog.Title>
          <Text size="6" weight="bold" mb="3" as="div">
            Manage brief access
          </Text>
        </Dialog.Title>
        <Box mb="4">
          {/* Brief Card */}
          <Box mb="4">
            <BriefCard brief={brief} />
          </Box>
        </Box>
        {!hasInitiallyLoaded ? <span>Loading...</span> : recipientsTable}
        {/* Footer */}
        <Flex gap="3" justify="end" mt="4">
          {!hasSelectedRecipients && (
            <Dialog.Close>
              <Button variant="soft" size="2">
                Close
              </Button>
            </Dialog.Close>
          )}

          {hasSelectedRecipients && (
            <Flex align="center">
              <Text mr="4" color="gray" size="2">{`${
                Object.keys(rowSelection).length
              } selected`}</Text>
              <SplitButton
                caption="Share with selected"
                onClick={handleShare}
                dropdownItems={buttonDropdownItems}
              />
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
export default ManageAccessModal
